import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Users2, Plus, Trash2, UserPlus, Mail, Palette, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_COLORS = [
  '#EF4444', '#F97316', '#EAB308', '#22C55E', '#14B8A6',
  '#3B82F6', '#6366F1', '#A855F7', '#EC4899', '#6B7280',
];

export function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [manageGroup, setManageGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', color: '#3B82F6', responsible_user_id: '' });
  const [addUserId, setAddUserId] = useState('');
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const [groupsRes, usersRes] = await Promise.all([
        axios.get(`${API}/groups`, { headers: getAuthHeader() }),
        axios.get(`${API}/users`, { headers: getAuthHeader() }),
      ]);
      setGroups(groupsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.name || !form.email) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      if (editGroup) {
        await axios.put(`${API}/groups/${editGroup.id}`, form, { headers: getAuthHeader() });
        toast.success('Grupo atualizado');
      } else {
        await axios.post(`${API}/groups`, form, { headers: getAuthHeader() });
        toast.success('Grupo criado');
      }
      setShowCreate(false);
      setEditGroup(null);
      setForm({ name: '', email: '', color: '#3B82F6', responsible_user_id: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar grupo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Excluir este grupo e todos os membros?')) return;
    try {
      await axios.delete(`${API}/groups/${groupId}`, { headers: getAuthHeader() });
      toast.success('Grupo excluído');
      fetchData();
    } catch (err) {
      toast.error('Erro ao excluir grupo');
    }
  };

  const handleAddMember = async () => {
    if (!addUserId || !manageGroup) return;
    try {
      await axios.post(`${API}/groups/${manageGroup.id}/members`, { user_id: addUserId }, { headers: getAuthHeader() });
      toast.success('Membro adicionado');
      setAddUserId('');
      fetchData();
      // Refresh manage group data
      const updated = await axios.get(`${API}/groups`, { headers: getAuthHeader() });
      const refreshed = updated.data.find(g => g.id === manageGroup.id);
      if (refreshed) setManageGroup(refreshed);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar membro');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!manageGroup) return;
    try {
      await axios.delete(`${API}/groups/${manageGroup.id}/members/${userId}`, { headers: getAuthHeader() });
      toast.success('Membro removido');
      fetchData();
      const updated = await axios.get(`${API}/groups`, { headers: getAuthHeader() });
      const refreshed = updated.data.find(g => g.id === manageGroup.id);
      if (refreshed) setManageGroup(refreshed);
    } catch (err) {
      toast.error('Erro ao remover membro');
    }
  };

  const openEdit = (group) => {
    setEditGroup(group);
    setForm({ name: group.name, email: group.email, color: group.color, responsible_user_id: group.responsible_user_id || '' });
    setShowCreate(true);
  };

  const openCreate = () => {
    setEditGroup(null);
    setForm({ name: '', email: '', color: '#3B82F6', responsible_user_id: '' });
    setShowCreate(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  // Members not yet in the manage group
  const availableUsers = manageGroup
    ? users.filter(u => u.is_active && !manageGroup.members?.some(m => m.user_id === u.id))
    : [];

  return (
    <div className="space-y-6" data-testid="groups-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Grupos de Usuários</h2>
          <p className="text-sm text-muted-foreground">Gerencie times e distribuição de notificações</p>
        </div>
        <Button onClick={openCreate} data-testid="create-group-btn">
          <Plus className="h-4 w-4 mr-2" />Novo Grupo
        </Button>
      </div>

      {groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users2 className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Nenhum grupo criado</p>
            <Button variant="outline" className="mt-4" onClick={openCreate}>Criar primeiro grupo</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => (
            <Card key={group.id} className="relative overflow-hidden" data-testid={`group-card-${group.id}`}>
              <div className="h-1.5" style={{ backgroundColor: group.color }} />
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                    <CardTitle className="text-base">{group.name}</CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(group)} data-testid={`edit-group-${group.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(group.id)} data-testid={`delete-group-${group.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />{group.email}
                </div>
                {group.responsible_name && (
                  <div className="text-sm text-muted-foreground">
                    Responsável: <span className="font-medium text-foreground">{group.responsible_name}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="text-xs">
                    <Users2 className="h-3 w-3 mr-1" />{group.member_count} membro{group.member_count !== 1 ? 's' : ''}
                  </Badge>
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setManageGroup(group)} data-testid={`manage-members-${group.id}`}>
                    <UserPlus className="h-3 w-3 mr-1" />Membros
                  </Button>
                </div>
                {group.members?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {group.members.slice(0, 5).map(m => (
                      <Badge key={m.id} variant="outline" className="text-xs">{m.user_name || m.user_email}</Badge>
                    ))}
                    {group.members.length > 5 && (
                      <Badge variant="outline" className="text-xs">+{group.members.length - 5}</Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(v) => { if (!v) { setShowCreate(false); setEditGroup(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editGroup ? 'Editar Grupo' : 'Novo Grupo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome do Grupo</Label>
              <Input placeholder="Ex: NOC, Infraestrutura, Suporte N2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name-input" />
            </div>
            <div className="space-y-2">
              <Label>E-mail de Distribuição</Label>
              <Input type="email" placeholder="time@empresa.com.br" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="group-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Responsável</Label>
              <Select value={form.responsible_user_id} onValueChange={(v) => setForm({ ...form, responsible_user_id: v })}>
                <SelectTrigger data-testid="group-responsible-select"><SelectValue placeholder="Selecionar usuário" /></SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.is_active).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Palette className="h-4 w-4" />Cor do Grupo</Label>
              <div className="flex gap-2 flex-wrap">
                {PRESET_COLORS.map(c => (
                  <button key={c} className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? 'border-foreground scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => setForm({ ...form, color: c })} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditGroup(null); }}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-group-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editGroup ? 'Salvar' : 'Criar Grupo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Members Dialog */}
      <Dialog open={!!manageGroup} onOpenChange={(v) => { if (!v) setManageGroup(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {manageGroup && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: manageGroup.color }} />}
              Membros — {manageGroup?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add member */}
            <div className="flex gap-2">
              <Select value={addUserId} onValueChange={setAddUserId}>
                <SelectTrigger className="flex-1" data-testid="add-member-select"><SelectValue placeholder="Selecionar usuário" /></SelectTrigger>
                <SelectContent>
                  {availableUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                  ))}
                  {availableUsers.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Todos os usuários já são membros</div>
                  )}
                </SelectContent>
              </Select>
              <Button onClick={handleAddMember} disabled={!addUserId} data-testid="add-member-btn">
                <UserPlus className="h-4 w-4" />
              </Button>
            </div>

            {/* Members list */}
            {manageGroup?.members?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manageGroup.members.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.user_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{m.user_email}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRemoveMember(m.user_id)} data-testid={`remove-member-${m.user_id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-6 text-sm text-muted-foreground">Nenhum membro neste grupo</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
